import React, { useState, useRef, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser, faSignOut, faCog } from '@fortawesome/pro-regular-svg-icons';
import { useAuth } from '../../../hooks';
import { getAuthUserDisplayName } from '../../../actions/login/OrganizationsLoginConverter';
import { logoutThunk } from '../../../actions/login/OrganizationsLoginActions';
import styles from './UserAvatar.module.scss';
import { Avatar } from './Avatar';
import MyAccountModal from '../../MyAccount/MyAccountModal';

interface UserAvatarProps {
    className?: string;
}

export const UserAvatar: React.FC<UserAvatarProps> = ({ className }) => {
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [isMyAccountOpen, setIsMyAccountOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const dispatch = useDispatch();
    const auth = useAuth();

    // Get user initials
    const getUserInitials = (): string => {
        if (!auth?.user) return '';

        const firstName = auth.user.firstName || '';
        const lastName = auth.user.lastName || '';

        if (firstName && lastName) {
            return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
        }

        if (firstName) {
            return firstName.charAt(0).toUpperCase();
        }

        if (auth.user.email) {
            return auth.user.email.charAt(0).toUpperCase();
        }

        return 'U';
    };

    // Get user display name
    const getUserName = (): string => {
        return getAuthUserDisplayName(auth);
    };

    // Get user role
    const getUserRole = (): string => {
        if (!auth?.user?.accessLevel) return 'User';
        return auth.user.accessLevel.name || 'User';
    };

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsDropdownOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleSignOut = () => {
        setIsDropdownOpen(false);
        dispatch(logoutThunk() as any);
    };

    const handleMyAccount = () => {
        setIsMyAccountOpen(true);
        setIsDropdownOpen(false);
    };

    return (
        <div
            className={`${styles.userAvatar} ${className || ''}`}
            ref={dropdownRef}
        >
            <button
                className={styles.avatarButton}
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                aria-label="User menu"
            >
                <Avatar initials={getUserInitials()} />
            </button>

            {isDropdownOpen && (
                <div className={styles.dropdown}>
                    <div className={styles.userInfo}>
                        <div className={styles.userName}>{getUserName()}</div>
                        <div className={styles.userRole}>{getUserRole()}</div>
                    </div>

                    <button
                        className={styles.menuItem}
                        onClick={handleMyAccount}
                    >
                        <FontAwesomeIcon icon={faUser} />
                        My account
                    </button>

                    <div className={styles.dividerContainer}>
                        <div className={styles.divider} />
                    </div>

                    <button
                        className={styles.menuItem}
                        onClick={handleSignOut}
                    >
                        Sign out
                    </button>
                </div>
            )}

            {/* My Account Modal */}
            <MyAccountModal
                isOpen={isMyAccountOpen}
                onClose={() => setIsMyAccountOpen(false)}
            />
        </div>
    );
};

export default UserAvatar;
